// PlayControls.tsx
import React, { FC, useEffect } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import Button from "../../../ui/components/Button";
import Slider from "../../../ui/components/Slider";
import SliderRange from "../../../ui/components/SliderRange";
import LabelRow from "../../../ui/components/LabelRow";

const PlayControlsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 5px;
    padding: 0;
    // padding: 0 6px 0 4px;
    margin: 0;
`;

const Row = styled.div<{ $isMobile: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-right: ${props => props.$isMobile ? "4px" : "0"};
    gap: 10px;
`;

const FramesLabelContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    min-width: 38px;
    max-width: 38px;
    gap: 1px;
    margin-right: 1px;
`;

const SemiBoldText = styled.p`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    font-family: ${Fonts.lexendSemiBold.fontFamily};
    font-weight: ${Fonts.lexendSemiBold.fontWeight};
    font-size: 13px;
    line-height: 1.2em;
    text-wrap: nowrap;
    color: ${Colors.grey5};
    margin: auto 0 auto 0;
`;

const LightText = styled.p`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    font-family: ${Fonts.lexendLight.fontFamily};
    font-weight: ${Fonts.lexendLight.fontWeight};
    font-size: 13px;
    line-height: 1.2em;
    text-wrap: nowrap;
    color: ${Colors.grey8};
    margin: auto 0 auto 0;
`;

interface PlayControlsProps {
    currentFrame: number;
    numFrames: number;
    // Added new range props
    startFrame: number;
    endFrame: number;
    handleFrameChange: (frame: number) => void;
    handleRangeChange: (start: number, end: number) => void; // New handler for start/end
    isPlaying: boolean;
    handlePlayPauseClick: () => void;
    isPlaceholder: boolean;
    isMobile: boolean;
}

const PlayControls: FC<PlayControlsProps> = ({ 
    currentFrame, 
    numFrames, 
    startFrame, // New prop
    endFrame, // New prop
    handleFrameChange, 
    handleRangeChange, // New prop
    isPlaying, 
    handlePlayPauseClick, 
    isPlaceholder,
    isMobile 
}) => {
    const handleSpaceKeyPress = (event: KeyboardEvent) => {
        if (event.key === " " && event.target === document.body) {
            event.preventDefault();
            handlePlayPauseClick();
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleSpaceKeyPress);
        return () => document.removeEventListener("keydown", handleSpaceKeyPress);
    }, [handlePlayPauseClick]);

    // Assuming the total duration is 2 seconds (as per original logic).
    const totalDuration = 2; // seconds
    // Calculate totalTime. If numFrames is 0, the result of the division is NaN.
    // Use the condition to check for 0 frames (or if the result is NaN) and default to 0 seconds.
    const totalTime = numFrames > 0 ? (currentFrame / numFrames) * totalDuration : 0;

    // Convert totalTime (in seconds) to 'M:SS' format.
    const minutes = Math.floor(totalTime / 60);
    const seconds = Math.floor(totalTime % 60);

    // Format seconds to always have two digits (e.g., '09', '10')
    const formattedSeconds = seconds.toString().padStart(2, '0');
    const timeLabel = `${minutes}:${formattedSeconds}`;

    // Values for SliderRange: [startRange, currentValue, endRange]
    const sliderRangeValue: [number, number, number] = [
        startFrame, // Start of playback range (0-indexed frame)
        currentFrame, // Current frame
        endFrame // End of playback range (max frame index)
    ];

    const handleInternalRangeChange = (newValue: [number, number, number]) => {
        const [newStart, newCurrent, newEnd] = newValue;
        
        // 1. Update the current frame only if it was the thumb moved
        const oldCurrent = sliderRangeValue[1];
        if (newCurrent !== oldCurrent) {
             handleFrameChange(newCurrent);
        }

        // 2. Update the range only if the start or end thumbs were moved
        const oldStart = sliderRangeValue[0];
        const oldEnd = sliderRangeValue[2];
        if (newStart !== oldStart || newEnd !== oldEnd) {
            handleRangeChange(newStart, newEnd);
        }
    };

    return (
        <PlayControlsContainer>
            <Row $isMobile={isMobile}>
                <Button
                    size="tiny"
                    color="black"
                    icon={isPlaying ? "pause" : "play"}
                    onClick={handlePlayPauseClick}
                    disabled={numFrames === 0 || isPlaceholder}
                />
                {/* <Slider
                    value={currentFrame}
                    min={0}
                    // The max should always be numFrames - 1 for 0-indexed frame count
                    max={numFrames - 1} 
                    step={1}
                    colorMode="light"
                    onChange={(newValue) => handleFrameChange(newValue)}
                    showLabel={false}
                    disabled={numFrames === 0}
                    hideKnobUntilHover={true}
                /> */}
                <SliderRange // Replaced Slider with SliderRange
                    value={sliderRangeValue}
                    min={0}
                    // The max should always be numFrames - 1 for 0-indexed frame count
                    max={numFrames > 0 ? numFrames - 1 : 0} 
                    step={1}
                    colorMode="light"
                    onChange={handleInternalRangeChange} // Updated onChange handler
                    showLabel={false}
                    disabled={numFrames === 0}
                    hideKnobUntilHover={false}
                    hideRangeKnobsUntilHover={false}
                    miniKnob={false} // Default value
                />
                {!isMobile && (
                    <FramesLabelContainer>
                        <SemiBoldText>{timeLabel}</SemiBoldText>
                        <LightText>{"s"}</LightText>
                    </FramesLabelContainer>
                )}
            </Row>
        </PlayControlsContainer>
    );
};

export default PlayControls;